import { Button, ButtonProps } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { tooltipIconButtonVariants } from '@/components/ui/tooltip-icon-button'
import { cn } from '@/lib/utils'
import { EllipsisVertical } from 'lucide-react'
import { T, useTranslate } from '@tolgee/react'
import React from 'react'
import { EditServiceRegistryDialog } from './edit-dialog'
import { DeleteServiceRegistryDialog } from './delete-dialog'
import { EditIcon } from '@/components/icons/edit-icon'
import { TrashIcon } from '@/components/icons/trash-icon'
import { ServiceRegistryTableDef, type ServiceToolsDialogData } from '../tab'
import { useServiceOAuthQueryState } from '../_hooks/use-service-oauth-query-state'

type ServiceRegistryOptionsMenuProps = Readonly<{
  serviceData: ServiceRegistryTableDef
  setToolsDialogData: React.Dispatch<React.SetStateAction<ServiceToolsDialogData>>
  setIsToolsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}>

function ServiceRegistryOptionsMenu({
  setIsToolsDialogOpen,
  setToolsDialogData,
  serviceData,
}: ServiceRegistryOptionsMenuProps) {
  const { serviceId, name: serviceName } = serviceData
  const { t } = useTranslate()
  const [open, setOpen] = React.useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)
  const { oauthParams, isOAuthSuccess, isOAuthError } = useServiceOAuthQueryState()

  const handleEditButtonClick = () => {
    setIsEditDialogOpen(true)
    setOpen(false)
  }

  const handleDeleteButtonClick = () => {
    setIsDeleteDialogOpen(true)
    setOpen(false)
  }

  React.useEffect(() => {
    if (
      (isOAuthSuccess || isOAuthError) &&
      oauthParams?.serviceId === serviceId &&
      !isEditDialogOpen
    ) {
      setIsEditDialogOpen(true)
    }
  }, [isOAuthSuccess, isOAuthError])

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          className={cn(tooltipIconButtonVariants())}
          title={t('serviceRegistry.optionsMenu.title')}
        >
          <EllipsisVertical className='text-neutral-150' />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='w-36 border border-white bg-neutral-25 p-1.5 shadow-sidebar'
        >
          <DropdownMenuItem asChild unstyled>
            <ServiceOptionButton
              onClick={handleEditButtonClick}
              title={t('serviceRegistry.optionsMenu.edit')}
            >
              <EditIcon className='shrink-0 text-neutral-150' />
              <T keyName='serviceRegistry.optionsMenu.edit' />
            </ServiceOptionButton>
          </DropdownMenuItem>
          <DropdownMenuItem asChild unstyled>
            <ServiceOptionButton
              onClick={handleDeleteButtonClick}
              className='text-error-300'
              title={t('serviceRegistry.optionsMenu.delete')}
            >
              <TrashIcon className='shrink-0 text-error-300' />
              <T keyName='serviceRegistry.optionsMenu.delete' />
            </ServiceOptionButton>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {isEditDialogOpen && (
        <EditServiceRegistryDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          setIsToolsDialogOpen={setIsToolsDialogOpen}
          setToolsDialogData={setToolsDialogData}
          serviceData={serviceData}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteServiceRegistryDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          serviceId={serviceId}
          serviceName={serviceName}
        />
      )}
    </>
  )
}

const ServiceOptionButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        unstyled
        className={cn(
          'flex h-10 w-full select-none items-center gap-3 rounded px-2 text-sm font-light tracking-wide outline-none hover:bg-neutral-75/75 focus:bg-neutral-75/75',
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    )
  },
)
ServiceOptionButton.displayName = 'ServiceOptionButton'

export { ServiceRegistryOptionsMenu, type ServiceRegistryOptionsMenuProps }
